/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface Args {
  stackVertically?: boolean;
  hideBorder?: boolean;
  hideTitle?: boolean;
  closeOnSave?: () => void;
  closeOnRevoke?: () => void;
  saveSuccessMessage?: string;
  requireConversationSyncing?: boolean;
}

export default class StandaloneZendeskConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare redirectService: any;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyError: (message: string) => void;
    notifyResponseError: (
      e: Error,
      messages: { default: string },
      options: { responseProvidedErrors: number[] },
    ) => void;
  };

  @tracked isSaving = false;
  @tracked isConnecting = false;
  private oauthWindow: Window | null | undefined = null;
  private oauthMessageHandler?: (
    event: MessageEvent<{ type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR'; error?: string }>,
  ) => void;
  private windowFocusHandler?: () => void;

  get saveSuccessMessageIdentifier() {
    return this.args.saveSuccessMessage ?? 'save-success';
  }

  get isSaveDisabled() {
    return (
      this.isSaving ||
      !this.zendeskConfig.canUpdateAPISettings ||
      (this.args.requireConversationSyncing &&
        !this.finStandaloneService.isConversationSyncingEnabled)
    );
  }

  get isConnectDisabled() {
    return (
      this.isConnecting ||
      !this.zendeskConfig.subdomain ||
      (this.args.requireConversationSyncing &&
        !this.finStandaloneService.isConversationSyncingEnabled)
    );
  }

  get zendeskConfig(): ZendeskConfiguration {
    return this.finStandaloneService.zendeskPlatform.zendeskConfig!;
  }

  @action updateSubdomain(event: Event) {
    let input = event.target as HTMLInputElement;
    let normalized = this.extractSubdomain(input.value);
    input.value = normalized;
    this.zendeskConfig.subdomain = normalized;
  }

  private extractSubdomain(input: string): string {
    let cleaned = input.trim();
    try {
      let url = new URL(cleaned.includes('://') ? cleaned : `https://${cleaned}`);
      if (url.hostname.endsWith('.zendesk.com')) {
        return url.hostname.replace('.zendesk.com', '');
      }
      return url.hostname;
    } catch {
      return cleaned;
    }
  }

  get tooltipMessageStepsToConnect() {
    return this.intl.t(
      this.args.requireConversationSyncing
        ? 'standalone.zendesk.settings.connect.tooltip-message-when-not-connected-and-syncing-required'
        : 'standalone.zendesk.settings.connect.tooltip-message-when-not-connected',
    );
  }

  get shouldShowOAuthUI() {
    return this.zendeskConfig?.shouldZendeskAPIShowOAuthUI;
  }

  get shouldShowOAuthReauthButton() {
    return (
      (this.zendeskConfig?.canUseZendeskOAuth || this.zendeskConfig?.usesOauth) &&
      !this.zendeskConfig?.shouldShowApiKeyFallbackBanner
    );
  }

  get shouldShowApiKeyFields() {
    return !this.shouldShowOAuthUI || this.zendeskConfig?.shouldShowApiKeyFallbackBanner;
  }

  get apiKeyFieldsEditable() {
    return (
      this.zendeskConfig?.isPendingSetupForTickets ||
      this.zendeskConfig?.shouldShowApiKeyFallbackBanner
    );
  }

  willDestroy() {
    super.willDestroy();
    this.resetOAuthState();
  }

  @action async save() {
    this.isSaving = true;
    try {
      this.zendeskConfig.isConversationSyncingEnabled =
        this.finStandaloneService.isConversationSyncingEnabled;
      await this.finStandaloneService.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t(`standalone.zendesk.settings.connect.${this.saveSuccessMessageIdentifier}`),
      );
      this.args.closeOnSave?.();
    } finally {
      this.isSaving = false;
    }
  }

  @action async saveApiKeyCredentials() {
    this.isSaving = true;
    try {
      await this.finStandaloneService.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.settings.connect.api-key-saved'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action async connectWithOAuth() {
    if (!this.zendeskConfig.subdomain) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.subdomain-required'),
      );
      return;
    }

    this.isConnecting = true;

    try {
      // Set up message handler for OAuth callback
      this.setupOAuthMessageHandler();
      // Set up timeout and window focus detection to handle cases where OAuth fails silently
      this.setupWindowFocusDetection();

      // Open OAuth flow in popup
      let uri = '/standalone/zendesk/connect';
      let params = `app_id_code=${this.finStandaloneService.app.id}&subdomain=${encodeURIComponent(this.zendeskConfig.subdomain)}&is_conversation_syncing_enabled=${this.finStandaloneService.isConversationSyncingEnabled}`;
      let oauthUrl = `${uri}?${params}`;

      // Open popup window for OAuth
      this.oauthWindow = safeWindowOpen(
        oauthUrl,
        'zendesk-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes',
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.oauth-error'),
      );
      this.isConnecting = false;
    }
  }

  private setupOAuthMessageHandler() {
    this.oauthMessageHandler = (
      event: MessageEvent<{ type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR'; error?: string }>,
    ) => {
      // Ensure the message is from our domain
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        this.handleOAuthCallback();
      } else if (event.data.type === 'OAUTH_ERROR') {
        this.handleOAuthCallback(event.data.error);
      }
    };

    window.addEventListener('message', this.oauthMessageHandler);
  }

  private setupWindowFocusDetection() {
    // Clear any existing handler
    if (this.windowFocusHandler) {
      window.removeEventListener('focus', this.windowFocusHandler);
    }

    this.windowFocusHandler = () => {
      // Check if OAuth window is still open
      if (this.oauthWindow && this.oauthWindow.closed) {
        // Window was closed manually, reset state
        this.resetOAuthState();
      }
    };

    window.addEventListener('focus', this.windowFocusHandler);
  }

  private resetOAuthState() {
    this.isConnecting = false;

    if (this.windowFocusHandler) {
      window.removeEventListener('focus', this.windowFocusHandler);
      this.windowFocusHandler = undefined;
    }

    if (this.oauthMessageHandler) {
      window.removeEventListener('message', this.oauthMessageHandler);
      this.oauthMessageHandler = undefined;
    }

    if (this.oauthWindow && !this.oauthWindow.closed) {
      this.oauthWindow.close();
      this.oauthWindow = null;
    }
  }

  private async handleOAuthCallback(error?: string) {
    // Clean up OAuth state immediately to prevent multiple calls
    this.resetOAuthState();

    if (error) {
      console.error(error);
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.oauth-failed', { error }),
      );
      return;
    }

    try {
      // Save zendesk config to get updated state.
      this.zendeskConfig.isConversationSyncingEnabled =
        this.finStandaloneService.isConversationSyncingEnabled;
      await this.finStandaloneService.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.settings.connect.oauth-success'),
      );
      this.args.closeOnSave?.();
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.config-reload-error'),
      );
    }
  }

  @action async revokeAccess() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.zendesk.settings.connect.confirm'),
      confirmButtonText: this.intl.t('standalone.zendesk.settings.connect.confirm-button'),
    });

    try {
      if (isConfirmed) {
        await this.zendeskConfig.revoke();
        if (this.args.closeOnRevoke) {
          this.args.closeOnRevoke();
        }
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.zendesk.settings.connect.revoke-success-notify'),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        { default: this.intl.t('standalone.zendesk.settings.connect.revoke-failure-notify') },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Settings::Connect': typeof StandaloneZendeskConnect;
    'standalone/zendesk/settings/connect': typeof StandaloneZendeskConnect;
  }
}
